// components/SettleBillModal.jsx
import React, { useState, useEffect } from "react";
import { settleBill } from "../api/orderApi";
import { useAuth } from "../context/AuthContext";

export default function SettleBillModal({ bill, onClose, onSettleComplete }) {
  const { user } = useAuth();
  // Safe Fallback Numbers
  const totalBill = Number(bill?.total_bill_amount || bill?.grandTotal || 0);
  const alreadyPaid = Number(bill?.settledAmount || bill?.settled_amount || 0);
  const remainingBalance = totalBill - alreadyPaid;

  // State variables matching your form inputs
  const [settlementType, setSettlementType] = useState("Full"); // "Full" or "Part"
  const [singlePaymentMode, setSinglePaymentMode] = useState("Cash"); // "Cash", "UPI", "PayTm", "Complimentary"
  const [singleAmountInput, setSingleAmountInput] = useState(remainingBalance.toFixed(2));
  const [loading, setLoading] = useState(false);

  // Part payment inputs
  const [cashPart, setCashPart] = useState("");
  const [upiPart, setUpiPart] = useState("");
  const [paytmPart, setPaytmPart] = useState("");
  const [zomatoPart, setZomatoPart] = useState("");
  const [compPart, setCompPart] = useState("");

  // Sum of all part inputs
  const dynamicPartTotal =
    parseFloat(cashPart || 0) +
    parseFloat(upiPart || 0) +
    parseFloat(paytmPart || 0) +
    parseFloat(zomatoPart || 0) +
    parseFloat(compPart || 0);

  const dynamicPartRemaining = remainingBalance - dynamicPartTotal;

  // Sync inputs when switching strategies
  useEffect(() => {
    if (settlementType === "Full") {
      setSingleAmountInput(remainingBalance.toFixed(2));
    } else {
      setCashPart(remainingBalance.toFixed(2));
      setUpiPart("");
      setPaytmPart("");
      setZomatoPart("");
      setCompPart("");
    }
  }, [settlementType, remainingBalance]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    // 1. Core verification checks
    if (settlementType === "Full") {
      const entered = parseFloat(singleAmountInput);
      if (isNaN(entered) || entered <= 0 || entered > remainingBalance) {
        alert("Please enter a valid payment amount.");
        return;
      }
    } else {
      if (dynamicPartTotal <= 0) {
        alert("Please allocate an amount to at least one payment mode.");
        return;
      }
      if (dynamicPartRemaining < -0.01) {
        alert("Overpayment detected!");
        return;
      }
    }

    // 2. BUILD THE PAYLOAD (Exactly as you described)
    let finalCash = 0;
    let finalUpi = 0;
    let finalPaytm = 0;
    let finalZomato = 0;
    let finalComplimentary = 0;
    let totalAmountPaid = 0;


    if (settlementType === "Full") {
      totalAmountPaid = parseFloat(singleAmountInput);

      // Set only the selected mode to the full amount; others stay 0
      if (singlePaymentMode === "Cash") finalCash = totalAmountPaid;
      if (singlePaymentMode === "UPI") finalUpi = totalAmountPaid;
      if (singlePaymentMode === "PayTm") finalPaytm = totalAmountPaid;
      if (singlePaymentMode === "Zomato") finalZomato = totalAmountPaid;
      if (singlePaymentMode === "Complimentary") finalComplimentary = totalAmountPaid;
    } else {
      // In part mode, read directly from the typed values
      finalCash = parseFloat(cashPart || 0);
      finalUpi = parseFloat(upiPart || 0);
      finalPaytm = parseFloat(paytmPart || 0);
      finalZomato = parseFloat(zomatoPart || 0);
      finalComplimentary = parseFloat(compPart || 0);
      totalAmountPaid = dynamicPartTotal;
    }

    const nextSettledTotal = alreadyPaid + totalAmountPaid;
    const nextStatus = Math.abs(totalBill - nextSettledTotal) < 0.01 ? "Settled" : "Part-Settled";

    const payload = {
      bill_Id: bill.bill_id,
      status: nextStatus,
      amountPaid: totalAmountPaid,
      settlementType: settlementType,
      paymentMode: settlementType === "Part" ? "partpayment" : singlePaymentMode,
      cash: finalCash,
      upi: finalUpi,
      paytm: finalPaytm,
      zomato: finalZomato,
      complimentary: finalComplimentary,
      // kot_Id_List: bill.kot_Id_List, // as we are calculating it from backend
      seatingName: bill.seatingName,
      restaurantId: user.restaurantId,
      update_by: user.id
    };

    // 3. Send payload to API
    const confirmed = window.confirm(`Are you sure you want to settle this bill with mode: ${settlementType}` );
    if (confirmed) {
      try {
        setLoading(true);
        const response = await settleBill(payload);
        const isSuccessful =
          response?.status === 200 ||
          response?.success === true ||
          response?.data?.success === true;
        if (isSuccessful) {
          //alert("Settlement saved successfully!");
          onSettleComplete(bill.id, nextStatus, totalAmountPaid);
        }
      } catch (error) {
        console.error(error);
        alert("Failed to submit settlement to server.");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col border border-gray-100">

        {/* Header */}
        <div className="p-5 border-b bg-gray-50 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-black text-gray-900">Finalize Bill Settlement</h3>
            <p className="text-xs text-gray-500">Table: <span className="font-bold text-gray-700">{bill.seatingName}</span></p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 font-bold text-xl" disabled={loading}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">

          {/* Balance Matrix */}
          <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 border rounded-xl text-center">
            <div>
              <span className="text-[10px] uppercase font-bold text-gray-400">Total Bill</span>
              <div className="text-base font-bold text-gray-800">₹{totalBill.toFixed(2)}</div>
            </div>
            <div className="border-x px-2">
              <span className="text-[10px] uppercase font-bold text-gray-400">Already Paid</span>
              <div className="text-base font-bold text-green-600">₹{alreadyPaid.toFixed(2)}</div>
            </div>
            <div>
              <span className="text-[10px] uppercase font-bold text-gray-400">Net Outstanding</span>
              <div className="text-lg font-black text-red-600">₹{remainingBalance.toFixed(2)}</div>
            </div>
          </div>

          {/* Strategy Selection */}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setSettlementType("Full")}
              className={`p-3 rounded-xl border font-bold text-sm transition ${settlementType === "Full" ? "bg-indigo-600 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
            >
              💰 Full Settlement (Single Mode)
            </button>
            <button
              type="button"
              onClick={() => setSettlementType("Part")}
              className={`p-3 rounded-xl border font-bold text-sm transition ${settlementType === "Part" ? "bg-indigo-600 text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
            >
              🔀 Part Settle / Split Payment
            </button>
          </div>

          {/* Render Full Strategy */}
          {settlementType === "Full" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4">
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase">Select Payment Mode</label>
                <div className="flex flex-col gap-2">
                  {["Cash", "UPI", "PayTm", "Complimentary", "Zomato"].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setSinglePaymentMode(mode)}
                      className={`p-2.5 rounded-xl border text-left text-sm font-bold flex justify-between transition ${singlePaymentMode === mode ? "bg-emerald-50 border-emerald-500 text-emerald-900" : "bg-white text-gray-700"
                        }`}
                    >
                      <span>{mode} Payment</span>
                      {singlePaymentMode === mode && <span className="text-emerald-600">✓</span>}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase">Amount Tendered</label>
                <div className="relative">
                  <span className="absolute left-3.5 top-2.5 text-gray-400 font-bold text-lg">₹</span>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full pl-8 pr-4 py-2 border rounded-xl font-black text-xl text-gray-900 bg-gray-50 focus:outline-none"
                    value={singleAmountInput}
                    onChange={(e) => setSingleAmountInput(e.target.value)}
                  />
                </div>
              </div>
            </div>
          ) : (
            /* Render Part/Split Strategy */
            <div className="border-t pt-4 space-y-4">
              <label className="text-xs font-bold text-gray-500 uppercase block">Allocate Split Amounts</label>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {/* Cash */}
                <div className="p-3 bg-gray-50 rounded-xl border space-y-1">
                  <span className="text-xs font-bold text-gray-700">💵 Cash</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    className="w-full px-2 py-1 border rounded-lg font-bold text-gray-800"
                    value={cashPart}
                    onChange={(e) => setCashPart(e.target.value)}
                  />
                </div>
                {/* UPI */}
                <div className="p-3 bg-gray-50 rounded-xl border space-y-1">
                  <span className="text-xs font-bold text-gray-700">📱 UPI</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    className="w-full px-2 py-1 border rounded-lg font-bold text-gray-800"
                    value={upiPart}
                    onChange={(e) => setUpiPart(e.target.value)}
                  />
                </div>
                {/* PayTm */}
                <div className="p-3 bg-gray-50 rounded-xl border space-y-1">
                  <span className="text-xs font-bold text-gray-700">💸 PayTm</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    className="w-full px-2 py-1 border rounded-lg font-bold text-gray-800"
                    value={paytmPart}
                    onChange={(e) => setPaytmPart(e.target.value)}
                  />
                </div>
                {/* Zomato */}
                <div className="p-3 bg-gray-50 rounded-xl border space-y-1">
                  <span className="text-xs font-bold text-gray-700">🍽️ Zomato</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    className="w-full px-2 py-1 border rounded-lg font-bold text-gray-800"
                    value={zomatoPart}
                    onChange={(e) => setZomatoPart(e.target.value)}
                  />
                </div>
                {/* Complimentary */}
                <div className="p-3 bg-gray-50 rounded-xl border space-y-1">
                  <span className="text-xs font-bold text-gray-700">🎁 Comp.</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    className="w-full px-2 py-1 border rounded-lg font-bold text-gray-800"
                    value={compPart}
                    onChange={(e) => setCompPart(e.target.value)}
                  />
                </div>
              </div>

              {/* Realtime Summary Info strip */}
              <div className="p-3 rounded-xl border border-dashed flex justify-between items-center text-xs font-medium bg-indigo-50/30">
                <div>Allocated: <span className="font-bold">₹{dynamicPartTotal.toFixed(2)}</span> / ₹{remainingBalance.toFixed(2)}</div>
                {dynamicPartRemaining > 0 ? (
                  <div className="text-amber-700 font-semibold">Remaining: ₹{dynamicPartRemaining.toFixed(2)}</div>
                ) : dynamicPartRemaining === 0 ? (
                  <div className="text-green-700 font-bold">✓ Perfect Balance</div>
                ) : (
                  <div className="text-red-700 font-bold">⚠️ Exceeded by ₹{Math.abs(dynamicPartRemaining).toFixed(2)}</div>
                )}
              </div>
            </div>
          )}

          {/* Footer controls */}
          <div className="flex gap-3 border-t pt-4 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 bg-gray-100 text-gray-700 text-sm font-semibold rounded-xl"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (settlementType === "Part" && dynamicPartRemaining < 0)}
              className={`px-6 py-2.5 text-white text-sm font-bold rounded-xl shadow-sm ${loading || (settlementType === "Part" && dynamicPartRemaining < 0) ? "bg-gray-300 cursor-not-allowed" : "bg-green-600"
                }`}
            >
              {loading ? "Saving..." : "Finalize Settlement ✓"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}